import { TaskTypeField, getTaskTypeFields } from '@/config/taskTypeFields';
import { DashboardType } from '@/config/dashboardWorkflows';
import TextField from './TaskFields/TextField';
import TextareaField from './TaskFields/TextareaField';
import NumberField from './TaskFields/NumberField';
import DateField from './TaskFields/DateField';
import DateTimeField from './TaskFields/DateTimeField';
import UrlField from './TaskFields/UrlField';
import EmailField from './TaskFields/EmailField';
import PhoneField from './TaskFields/PhoneField';
import CurrencyField from './TaskFields/CurrencyField';
import DropdownField from './TaskFields/DropdownField';
import RangeField from './TaskFields/RangeField';
import { useState, useEffect } from 'react';

interface TaskTypeFieldsProps {
  dashboardType: DashboardType | null;
  cardType: number | null;
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  teamMembers?: Array<{ id: number; name: string; email: string; avatar: string }>;
  onFetchTeamMembers?: () => Promise<Array<{ id: number; name: string; email: string; avatar: string }>>;
}

export default function TaskTypeFields({
  dashboardType,
  cardType,
  data,
  onChange,
  errors = {},
  teamMembers = [],
}: TaskTypeFieldsProps) {
  const config = getTaskTypeFields(dashboardType, cardType);

  if (!config) {
    return null;
  }

  const handleFieldChange = (fieldName: string, value: unknown) => {
    onChange({
      ...data,
      [fieldName]: value,
    });
  };

  const renderField = (field: TaskTypeField) => {
    const value = data[field.name] || '';
    const error = errors[field.name];

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            placeholder={field.placeholder}
            error={error}
          />
        );
      case 'textarea':
        return (
          <TextareaField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            placeholder={field.placeholder}
            rows={field.rows}
            error={error}
          />
        );
      case 'number':
        return (
          <NumberField
            key={field.name}
            label={field.label}
            value={value === '' || value === null || value === undefined ? '' : (value as number)}
            onChange={(val) => handleFieldChange(field.name, val === '' ? null : val)}
            required={field.required}
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            error={error}
          />
        );
      case 'date':
        return (
          <DateField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            error={error}
          />
        );
      case 'url':
        return (
          <UrlField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            placeholder={field.placeholder}
            error={error}
          />
        );
      case 'dropdown':
        return (
          <DropdownField
            key={field.name}
            label={field.label}
            value={value as string | number | null}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            options={field.options || []}
            error={error}
          />
        );
      case 'range':
        return (
          <RangeField
            key={field.name}
            label={field.label}
            value={value === '' || value === null || value === undefined ? '' : (value as number)}
            onChange={(val) => handleFieldChange(field.name, val === '' ? null : val)}
            required={field.required}
            min={field.min}
            max={field.max}
            error={error}
          />
        );
      case 'datetime':
        return (
          <DateTimeField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            error={error}
          />
        );
      case 'user_select':
        // Use dropdown with team members as options
        return (
          <DropdownField
            key={field.name}
            label={field.label}
            value={value as number | null}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            options={teamMembers.map(member => ({
              value: member.id,
              label: member.name,
            }))}
            error={error}
          />
        );
      case 'email':
        return (
          <EmailField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            placeholder={field.placeholder}
            error={error}
          />
        );
      case 'phone':
        return (
          <PhoneField
            key={field.name}
            label={field.label}
            value={value as string}
            onChange={(val) => handleFieldChange(field.name, val)}
            required={field.required}
            placeholder={field.placeholder}
            error={error}
          />
        );
      case 'currency':
        return (
          <CurrencyField
            key={field.name}
            label={field.label}
            value={value === '' || value === null || value === undefined ? '' : (value as number)}
            onChange={(val) => handleFieldChange(field.name, val === '' ? null : val)}
            required={field.required}
            placeholder={field.placeholder}
            error={error}
          />
        );
      default:
        return null;
    }
  };

  // Filter out 'title' field since it's handled separately in the modal
  const fieldsToRender = config.fields.filter(field => field.name !== 'title');

  if (fieldsToRender.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 mb-6">
      <div className="border-t border-[#1a1f3a] pt-4">
        <div className="space-y-4">
          {fieldsToRender.map(renderField)}
        </div>
      </div>
    </div>
  );
}

